import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return null;
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error(t('auth.errors.passwordMismatch')); return; }
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      toast.error(t('auth.errors.passwordWeak')); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, password }, { withCredentials: true });
      toast.success(t('auth.reset.success'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t('auth.reset.invalid'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex flex-col">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex justify-between items-center">
          <Link to="/"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-3"><DarkModeToggle /><LanguageSwitcher variant="minimal" /></div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8">
          <h1 data-testid="reset-title" className="font-primary font-bold text-2xl md:text-3xl text-slate-900 dark:text-white mb-2">
            {t('auth.reset.title')}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{t('auth.reset.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">{t('auth.newPassword')}</Label>
              <Input id="password" data-testid="reset-password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
              <p className="mt-1 text-xs text-slate-500">{t('auth.signup.passwordHint')}</p>
            </div>
            <div>
              <Label htmlFor="confirm">{t('auth.confirmPassword')}</Label>
              <Input id="confirm" data-testid="reset-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" />
            </div>
            <Button data-testid="reset-submit" type="submit" disabled={submitting || !token}
              className="w-full bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
              {submitting ? t('auth.reset.submitting') : t('auth.reset.submit')}
            </Button>
            {!token && <p className="text-sm text-red-500">{t('auth.reset.missingToken')}</p>}
          </form>
        </div>
      </div>
    </div>
  );
}
